import PropTypes from "prop-types"
import * as DropdownMenu from "@radix-ui/react-dropdown-menu"
import { cn } from "../../../lib/utils"

const getInitials = (name) => {
  if (!name) return "?"
  const parts = name.trim().split(/\s+/)
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const itemShape = PropTypes.shape({
  type: PropTypes.string,
  separator: PropTypes.bool,
  label: PropTypes.string,
  onClick: PropTypes.func,
  icon: PropTypes.node,
  disabled: PropTypes.bool,
  danger: PropTypes.bool,
  "data-testid": PropTypes.string,
})

export default function AppDropdownMenu({
  trigger,
  items = [],
  side = "bottom",
  align = "end",
}) {
  // Separators carry no label, so key them by the preceding labelled item
  let lastLabel = ""
  const itemKey = (item) => {
    if (item.separator) return `separator-after-${lastLabel}`
    lastLabel = item.label
    return `${item.type === "header" ? "header" : "item"}-${item.label}`
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={cn(
            // explicit solid bg — avoids Radix portal transparency issue
            "bg-[var(--color-panel-solid,#ffffff)]",
            "border border-[var(--gray-6,#e4e4e7)]",
            "rounded-[var(--radius-md,8px)]",
            "shadow-[0_8px_32px_rgba(0,0,0,0.16)]",
            "isolate",
            "p-1",
            "min-w-[200px]",
            "z-[1050]",
            "animate-dropdown-in",
          )}
          side={side}
          align={align}
          sideOffset={4}
        >
          {items.map((item) => {
            const key = itemKey(item)
            if (item.type === "header") {
              return (
                <div
                  key={key}
                  className={cn(
                    "flex items-center gap-[10px]",
                    "px-[14px] py-[10px]",
                    "bg-[var(--gray-3)]", // Radix auto-switch: light ≈ #F0F0F0 / dark ≈ #252525
                    "rounded-t-[var(--radius-sm,4px)]",
                    "border-b border-[var(--gray-6,#e4e4e7)]",
                    "mb-1",
                  )}
                >
                  <span
                    className={cn(
                      "w-[30px] h-[30px]",
                      "rounded-full",
                      "bg-[var(--brand-green)]", // brand green
                      "text-[var(--nav-bg)]", // brand nav dark
                      "text-[11px] font-bold",
                      "flex items-center justify-center",
                      "shrink-0",
                      "tracking-[0.03em]",
                    )}
                  >
                    {getInitials(item.label)}
                  </span>
                  <span
                    className={cn(
                      "text-[0.82rem] font-semibold",
                      "text-[var(--text-heading)]",
                      "whitespace-nowrap overflow-hidden text-ellipsis",
                    )}
                  >
                    {item.label}
                  </span>
                </div>
              )
            }

            if (item.separator) {
              return (
                <DropdownMenu.Separator
                  key={key}
                  className="h-px bg-[var(--border-subtle-2)] my-1"
                />
              )
            }

            return (
              <DropdownMenu.Item
                key={key}
                className={cn(
                  "flex items-center gap-2",
                  "px-3 py-2",
                  "text-[0.82rem]",
                  "cursor-pointer",
                  "rounded-[var(--radius-sm)]",
                  "text-[var(--text-body)]",
                  "outline-none",
                  "transition-colors duration-100",
                  "data-[highlighted]:bg-[var(--gray-3)]",
                  "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed",
                  item.danger && [
                    "data-[highlighted]:text-[var(--red-11)]",
                    "data-[highlighted]:bg-[var(--red-3)]",
                  ],
                )}
                onSelect={item.onClick}
                disabled={item.disabled}
                {...(item["data-testid"] ? { "data-testid": item["data-testid"] } : {})}
              >
                {item.icon && (
                  <span className="flex items-center text-[var(--text-secondary)] shrink-0">
                    {item.icon}
                  </span>
                )}
                {item.label}
              </DropdownMenu.Item>
            )
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

AppDropdownMenu.propTypes = {
  trigger: PropTypes.node.isRequired,
  items: PropTypes.arrayOf(itemShape),
  side: PropTypes.oneOf(["top", "right", "bottom", "left"]),
  align: PropTypes.oneOf(["start", "center", "end"]),
}
